<template>
  <div>
    <b-modal v-model="openCourseModal" :title="courseModalTitle" size="lg" @ok.prevent="attemptCourseSubmit = true"
      @cancel="editingCourse = false">
      <course-form v-model="attemptCourseSubmit" :editingCourse="editingCourse ? selectedCourse : null"
        v-on:complete="processCourseReturn" v-if="openCourseModal"></course-form>
    </b-modal>

    <b-modal v-model="confirmingCourseDeletion" title="Confirm Delete" v-if="selectedTimePeriodTerm && selectedCourse"
      @ok="removeCourse()" @cancel="clearCourseDeleteInfo()">
      Are you sure you want to delete {{ selectedCourse.subjectCode }}
      {{ selectedCourse.catalogNumber }} - {{ selectedCourse.courseTitle }} from
      {{ selectedTimePeriodTerm.term.description }}?
    </b-modal>

    <b-alert :show="workload.asuriteId !== user.userId" variant="warning">You are submitting a workload on behalf of
      {{ workload.asuriteId }}</b-alert>

    <b-alert variant="info" show><strong>New for 2023 - 2024:</strong>

      <p>
        <strong>Clinical Percent</strong> - Clinical effort occurs when faculty
        are providing non-teaching clinical services (without additional
        compensation) as part of their established workload. Services provided
        under a Notice of Consulting are not considered as part of workload.
      </p>

      <p>
        <strong>Administration Assignment Requests</strong> - Administration
        assignment requests are required to account for any administration
        percentage. Multiple requests may be submitted per workload, but should
        total and not exceed the administration value for the workload.
      </p>
    </b-alert>

    <b-container fluid style="margin-left: 10px; padding-top: 10px; width: 60%">
      <b-row>
        <!-- <b-col :sm="12 / workload.workloadTypes.length" v-for="workloadType in workload.workloadTypes" :key="workloadType.workloadTypeDefinitionId"> -->
        <b-col sm v-for="workloadType in workload.workloadTypes" :key="workloadType.workloadTypeDefinitionId">
          <b-form-group :label="workloadType.workloadTypeDefinition.name + ' (%)'" label-class="workload-type-label">
            <b-form-input class="workload-type-input" type="number" :value="(workloadType.value * 100).toFixed(0)"
              @input="updateWorkloadTypeValue(workloadType, $event)"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-container>

    <b-alert show :variant="adminPercentMatches ? 'info' : 'danger'">
      You must add administrative assignment requests to account for your total
      administrative time. You have accounted for {{ totalAdminPercent }}% of
      {{ administrativePercent }}% total administrative percent.
    </b-alert>

    <div>
      <label for="total-percentage" class="total-label mr-2">Total: </label>
      <span id="total-percentage">{{ totalPercentage | percentage }}%</span>
    </div>

    <b-alert variant="danger" :show="totalPercentage < 1">Warning: Your total workload percentage is below 100%.</b-alert>
    <b-alert variant="warning" :show="totalPercentage > 1">Warning: Your total workload percentage is above
      100%.</b-alert>
    <b-alert variant="success" :show="totalPercentage == 1">Your total workload percentage is 100%.</b-alert>

    <div class="mt-3">
      <h4>Courses</h4>
      <p>
        Please note that course selections here are tentative. If your courses
        later change, your workload is still valid as long as the workload
        percentages stay the same.
      </p>
      <b-alert variant="warning" :show="contributingCourseCount !== coursesNeeded">
        Warning: You need {{ coursesNeeded - contributingCourseCount }} more
        course{{ coursesNeeded - contributingCourseCount > 1 ? "s" : "" }} to
        match your teaching percentage.
      </b-alert>

      <b-row v-if="workload.timePeriod">
        <template v-for="timePeriodTerm in workload.timePeriod.timePeriodTerms">
          <b-col sm="4" :key="timePeriodTerm.termId">
            <b-card :title="timePeriodTerm.term.description" class="my-1 workload-term-card">
              <b-alert variant="info" :show="isAcadYear && timePeriodTerm.term.termCode.endsWith('4')">Academic year
                faculty are not required to enter summer
                classes.</b-alert>

              <div class="no-courses-text" v-if="!termCourses[timePeriodTerm.termId].length">
                No classes have been added for this semester.
              </div>

              <b-table striped hover small :fields="courseFields" :items="termCourses[timePeriodTerm.termId]"
                v-if="termCourses[timePeriodTerm.termId].length">
                <template v-slot:cell(contributesToTeaching)="data">
                  <font-awesome-icon icon="minus-circle" v-b-tooltip.hover
                    title="This course does not contribute to Teaching %."
                    v-if="!data.item.contributesToTeaching"></font-awesome-icon>
                </template>
                <template v-slot:cell(new)="data">
                  <font-awesome-icon icon="exclamation-triangle" v-b-tooltip.hover
                    title="This course does not match any courses currently in the database."
                    v-if="data.item.new"></font-awesome-icon>
                </template>
                <template v-slot:cell(course)="data">
                  {{ data.item.subjectCode }} {{ data.item.catalogNumber }} -
                  {{ data.item.courseTitle }}
                </template>
                <template v-slot:cell(notes)="data">
                  <div class="text-right" v-if="data.item.notes">
                    <font-awesome-icon icon="sticky-note" v-b-tooltip.hover :title="data.item.notes"></font-awesome-icon>
                  </div>
                </template>
                <template v-slot:cell(controls)="data">
                  <div class="text-right">
                    <font-awesome-icon icon="pencil-alt" class="edit-icon mx-1"
                      @click="openCourseEdit(timePeriodTerm, data.item)"></font-awesome-icon>
                    <font-awesome-icon icon="times" class="remove-icon mx-1"
                      @click="confirmCourseDeletion(timePeriodTerm, data.item)"></font-awesome-icon>
                  </div>
                </template>
              </b-table>

              <div class="text-right">
                <b-button @click="openCourseAdd(timePeriodTerm)">Add Course</b-button>
              </div>
              <div class="mt-2" v-if="!(isAcadYear && timePeriodTerm.term.termCode.endsWith('4'))
                ">
                <b-form-checkbox v-model="overages[timePeriodTerm.termId]"
                  @input="setOverageRequest(timePeriodTerm.termId)">I am interested in a teaching overload for this
                  semester if
                  available.</b-form-checkbox>
              </div>
            </b-card>
          </b-col>
        </template>
      </b-row>
    </div>

    <div class="mt-3" v-if="workload.timePeriod">
      <h4>Buyout Requests</h4>

      <workload-buyouts :buyouts="workload.buyoutRequests" :title="buyoutModalTitle"
        :terms="workload.timePeriod.timePeriodTerms" @added="addBuyoutRequest"
        @removed="removeBuyoutRequest"></workload-buyouts>
    </div>

    <div class="mt-3" v-if="workload.timePeriod">
      <h4>Administration Assignment Requests</h4>

      <b-alert show :variant="adminPercentMatches ? 'info' : 'danger'">
        You must add administrative assignment requests to account for your
        total administrative time. You have accounted for
        {{ totalAdminPercent }}% of {{ administrativePercent }}% total
        administrative percent.
      </b-alert>

      <workload-admin-requests :requests="workload.administrativeRequests" :title="adminModalTitle"
        @added="addAdminRequest" @removed="removeAdminRequest"
        :adminPercent="administrativePercent"></workload-admin-requests>
    </div>

    <div class="mt-3" v-if="workload.timePeriod">
      <h4>Course Equivalents</h4>

      <b-alert show variant="info">
        Course equivalents should be discussed and approved in writing by the relevant Assistant/Associate Deans before
        being added to your workload. Please upload this documentation with your course equivalent submission.
      </b-alert>

      <workload-course-equivalents :courseEquivalents="courseEquivalents" :terms="workloadTerms"
        :workload-faculty="workload.asuriteId" @refresh="loadCourseEquivalents"></workload-course-equivalents>
    </div>

    <div v-for="workloadType in workload.workloadTypes" :key="workloadType.workloadTypeDefinitionId" class="mt-3">
      <div v-if="workloadType.workloadTypeDefinition.name != 'Teaching'">
        <h4>{{ workloadType.workloadTypeDefinition.name }}</h4>
        <p v-if="workloadType.workloadTypeDefinition.name == 'Research'">
          If you have a course buyout, use the textbox below to explain your
          funding for this. Please include details such as grant, title, and
          account number.
        </p>
        <p v-if="workloadType.workloadTypeDefinition.name == 'Service'">
          Use this textbox to list any service committees or other planned
          service contributions.
        </p>
        <p v-if="workloadType.workloadTypeDefinition.name == 'Administration'">
          Use this textbox to list any administrative roles you may have.
        </p>
        <b-form-textarea :rows="3" placeholder="Enter text..." v-model="workloadType.notes"
          @input="updateWorkloadTypeNotes(workloadType, $event)"></b-form-textarea>
      </div>
    </div>

    <div class="mt-3">
      <div>
        <h4>Supporting Documents</h4>
        <workload-files :editable="true" :files="mappedFiles" @fileAdded="addFile"
          @fileRemoved="removeFile"></workload-files>
      </div>
    </div>

    <b-alert show :variant="statusAlertVariant" class="my-2 mx-2" v-if="currentWorkloadStatus">
      <span>Current Status:
        <span class="font-weight-bold">{{
          currentWorkloadStatus.workloadStatusDefinition.name
        }}</span></span>
      <div v-if="currentWorkloadStatus.notes" class="mx-4">
        <hr />
        Comments:
        <span class="font-italic">{{ currentWorkloadStatus.notes }}</span>
      </div>
    </b-alert>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import toastr from "toastr";
import ToastrSettings from "../../settings/toastr-settings";
import CourseForm from "@/components/courses/course-form";
import WorkloadBuyouts from "@/components/workloads/workload-buyouts";
import WorkloadAdminRequests from "@/components/workloads/workload-admin-requests";
import WorkloadFiles from "@/components/workloads/workload-files";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars
import WorkloadCourseEquivalents from "@/components/workloads/workload-course-equivalents";

export default {
  data() {
    return {
      openCourseModal: false,
      editingCourse: false,
      confirmingCourseDeletion: false,
      selectedTimePeriodTerm: null,
      selectedCourse: null,
      selectedCourseIndex: null,
      attemptCourseSubmit: false,
      overages: [],
      courseFields: [
        { key: "new", label: "" },
        { key: "contributesToTeaching", label: "" },
        { key: "course" },
        { key: "units" },
        { key: "notes", label: "" },
        { key: "controls", label: "" }
      ]
    };
  },

  components: {
    "course-form": CourseForm,
    WorkloadFiles,
    WorkloadBuyouts,
    WorkloadAdminRequests,
    WorkloadCourseEquivalents
  },

  computed: {
    ...mapState({
      workload: state => state.workloadForm.workload,
      employeeJobs: state => state.workloadForm.employeeJobs,
      files: state => state.workloadForm.files,
      user: state => state.auth.user,
      courseEquivalents: state => state.workloadForm.courseEquivalents
    }),

    isAcadYear() {
      return (
        this.primaryEmployeeJob &&
        this.primaryEmployeeJob.paygroup !== "FSC" &&
        this.primaryEmployeeJob.paygroup !== "SAL"
      );
    },

    mappedFiles() {
      return _.map(this.workload.workloadFiles, function (workloadFile) {
        return {
          fileId: workloadFile.file.fileId,
          name: workloadFile.file.fileName,
          createdBy: workloadFile.file.createdBy,
          createdDate: workloadFile.file.createdDate,
          _rowVariant: workloadFile.file._rowVariant
            ? workloadFile.file._rowVariant
            : null
        };
      });
    },

    currentWorkloadStatus: function () {
      if (this.workload) {
        var currentStatus = _.maxBy(this.workload.workloadStatuses, function (
          workloadStatus
        ) {
          return new Date(workloadStatus.createdDate);
        });
        return currentStatus;
      }
      return null;
    },

    statusAlertVariant: function () {
      if (this.currentWorkloadStatus) {
        if (
          this.currentWorkloadStatus.workloadStatusDefinition.name ===
          "Approved"
        ) {
          return "success";
        }
        if (
          this.currentWorkloadStatus.workloadStatusDefinition.name === "Denied"
        ) {
          return "danger";
        }
        if (
          this.currentWorkloadStatus.workloadStatusDefinition.name ===
          "Resubmit"
        ) {
          return "warning";
        }
      }
      return "info";
    },

    primaryEmployeeJob: function () {
      if (!this.employeeJobs.length) {
        return null;
      }
      var primary = _.find(this.employeeJobs, function (employeeJob) {
        return employeeJob.jobIndicator === "P";
      });
      if (primary) {
        return primary;
      }
      return this.employeeJobs[0];
    },

    totalPercentage: function () {
      var total = 0;

      _.forEach(this.workload.workloadTypes, function (workloadType) {
        if (workloadType.value) {
          total += workloadType.value;
        }
      });
      total = Math.round((total + Number.EPSILON) * 100) / 100;
      return total;
    },

    totalAdminPercent: function () {
      if (!this.workload.administrativeRequests) return 0;
      return _.sumBy(this.workload.administrativeRequests, request => {
        return parseFloat(request.administrativePercent);
      });
    },

    administrativePercent: function () {
      return (
        _.filter(this.workload.workloadTypes, type => {
          return type.workloadTypeDefinition.name === "Administration";
        })[0].value * 100
      );
    },

    adminPercentMatches: function () {
      return this.totalAdminPercent === this.administrativePercent;
    },

    courseModalTitle: function () {
      var title = "";
      if (this.selectedTimePeriodTerm && this.editingCourse) {
        return (
          "Editing course in " + this.selectedTimePeriodTerm.term.description
        );
      } else if (this.selectedTimePeriodTerm) {
        return (
          "Adding course to " + this.selectedTimePeriodTerm.term.description
        );
      }

      return title;
    },

    buyoutModalTitle: function () {
      var title = "";
      if (this.workload.timePeriod && this.editingBuyout) {
        return "Editing buyout in " + this.workload.timePeriod.description;
      } else if (this.workload.timePeriod) {
        return "Adding buyout to " + this.workload.timePeriod.description;
      }

      return title;
    },

    adminModalTitle: function () {
      var title = "";
      if (this.workload.timePeriod && this.editingAdminRequest) {
        return (
          "Editing administrative time request in " +
          this.workload.timePeriod.description
        );
      } else if (this.workload.timePeriod) {
        return (
          "Adding administrative time request to " +
          this.workload.timePeriod.description
        );
      }

      return title;
    },

    approvedCourseEquivalents: function () {
      const approved = _.filter(this.courseEquivalents, function (
        courseEquivalent
      ) {
        return courseEquivalent.status === "Approved";
      });

      const numberOfCourses = _.sumBy(approved, function (courseEquivalent) {
        return courseEquivalent.numberOfCourses;
      });

      return numberOfCourses || 0;
    },

    coursesNeeded: function () {
      var offset = 0 - this.approvedCourseEquivalents;
      var courses = 0;
      var fte = this.primaryEmployeeJob ? this.primaryEmployeeJob.fte : 1;
      var teachingWorkloadType = _.find(this.workload.workloadTypes, function (
        workloadType
      ) {
        return workloadType.workloadTypeDefinition.name === "Teaching";
      });
      if (teachingWorkloadType && this.primaryEmployeeJob) {
        if (
          this.primaryEmployeeJob.paygroup === "FSW" ||
          this.primaryEmployeeJob.paygroup === "SAL"
        ) {
          var roundedToFive =
            Math.round((teachingWorkloadType.value * 100) / 5) * 5;

          if (roundedToFive <= 0) {
            courses = 0;
          } else if (roundedToFive <= 10) {
            courses = 1;
          } else if (roundedToFive <= 20) {
            courses = 2;
          } else if (roundedToFive <= 25) {
            courses = 3;
          } else if (roundedToFive <= 30) {
            courses = 4;
          } else if (roundedToFive <= 40) {
            courses = 5;
          } else if (roundedToFive <= 50) {
            courses = 6;
          } else if (roundedToFive <= 55) {
            courses = 7;
          } else if (roundedToFive <= 65) {
            courses = 8;
          } else if (roundedToFive <= 70) {
            courses = 9;
          } else if (roundedToFive <= 80) {
            courses = 10;
          } else if (roundedToFive <= 85) {
            courses = 11;
          } else if (roundedToFive > 85) {
            courses = 12;
          }

          return Math.round(courses * fte) + offset;
        } else {
          courses = Math.ceil(teachingWorkloadType.value * 10);
          return Math.round(courses * fte) + offset;
        }
      }
      return 0;
    },

    // number of courses that contribute to Teaching %
    contributingCourseCount: function () {
      var count = 0;
      _.forEach(this.workload.courses, function (course) {
        if (course.contributesToTeaching) {
          count += 1;
        }
      });
      return count;
    },

    // dictionary with termId as key and array of courses as value
    termCourses: function () {
      var that = this;
      var returnDict = {};
      _.forEach(this.workload.timePeriod.timePeriodTerms, function (
        timePeriodTerm
      ) {
        returnDict[timePeriodTerm.termId] = _.filter(
          that.workload.courses,
          function (course) {
            return course.termId === timePeriodTerm.termId;
          }
        );
      });
      return returnDict;
    },

    termOverageRequests: function () {
      var returnDict = {};
      _.forEach(this.workload.timePeriod.timePeriodTerms, timePeriodTerm => {
        returnDict[timePeriodTerm.termId] = _.filter(
          this.workload.overageRequests,
          function (req) {
            return req.termId === timePeriodTerm.termId;
          }
        )[0];
      });
      return returnDict;
    },

    workloadTerms: function () {
      return _.map(this.workload.timePeriod.timePeriodTerms, function (term) {
        return term.term;
      });
    }
  },
  methods: {
    ...mapActions("workloadForm", ["getCourseEquivalents"]),
    loadCourseEquivalents() {
      this.getCourseEquivalents({
        timePeriod: this.workload.timePeriod,
        asuriteId: this.workload.asuriteId
      });
    },
    openCourseAdd: function (timePeriodTerm) {
      this.selectedTimePeriodTerm = timePeriodTerm;
      this.openCourseModal = true;
    },

    openCourseEdit: function (timePeriodTerm, course) {
      this.selectedTimePeriodTerm = timePeriodTerm;
      this.selectedCourse = course;
      this.editingCourse = true;
      this.openCourseModal = true;
    },

    processCourseReturn: function (returnedCourse) {
      this.attemptCourseSubmit = false;
      if (returnedCourse) {
        returnedCourse.termId = this.selectedTimePeriodTerm.termId;
        if (this.editingCourse) {
          this.submitEditCourse(returnedCourse);
        } else {
          this.submitNewCourse(returnedCourse);
        }
        this.openCourseModal = false;
        this.selectedTimePeriodTerm = null;
      }
      this.editingCourse = false;
    },

    setOverageRequest: function (termId) {
      var existing = _.filter(this.workload.overageRequests, req => {
        return req.termId === termId;
      });
      // value has been switched to true
      if (this.overages[termId]) {
        if (existing.length === 0) {
          this.workload.overageRequests.push({
            id: 0,
            workloadId: this.workloadId,
            termId: termId,
            asuriteId: this.workload.asuriteId
          });
        }
        // value has been switched to false
      } else {
        this.workload.overageRequests = _.filter(
          this.workload.overageRequests,
          req => {
            return req.termId != termId;
          }
        );
      }
    },

    addBuyoutRequest: function (buyout) {
      //buyout.workloadId = this.workload.workloadId;
      if (!buyout.statusId) {
        // initialize status
        buyout.statusId = 0;
        buyout.currentStatus = {
          statusName: "Not Submitted"
        };

        // set the term description so it can be readable on the workload form
        buyout.term.description = _.filter(
          this.workload.timePeriod.timePeriodTerms,
          term => {
            return term.termId == buyout.termId;
          }
        )[0].term.description;
      }
      this.workload.buyoutRequests.push(buyout);
      //this.submitNewBuyout(buyout);
    },

    addAdminRequest: function (adminRequest) {
      if (!adminRequest.statusId) {
        // initialize status
        adminRequest.statusId = 0;
        adminRequest.currentStatus = {
          statusName: "Not Submitted"
        };
      }
      this.workload.administrativeRequests.push(adminRequest);
    },

    editBuyoutRequest: function (buyout) { },

    removeBuyoutRequest: function (buyout) {
      var index = _.findIndex(this.workload.buyoutRequests, item => {
        if (buyout.id) {
          return buyout.id === item.id;
        } else {
          return (
            item.notes === buyout.notes &&
            _.isEqual(item.grants, buyout.grants) &&
            item.buyoutPercent === buyout.buyoutPercent
          );
        }
      });
      this.workload.buyoutRequests.splice(index, 1);
    },

    removeAdminRequest: function (adminRequest) {
      var index = _.findIndex(this.workload.administrativeRequests, item => {
        if (adminRequest.id) {
          return adminRequest.id === item.id;
        } else {
          return (
            item.notes === adminRequest.notes &&
            item.administrativePercent === adminRequest.administrativePercent
          );
        }
      });
      this.workload.administrativeRequests.splice(index, 1);
    },

    submitNewCourse: function (newCourse) {
      this.workload.courses.push(newCourse);
      this.$store.commit(
        "workloadForm/updateWorkloadCourses",
        this.workload.courses
      );

      // Toastr Message
      toastr.options = ToastrSettings;
      toastr.success(
        "Course added to " + this.selectedTimePeriodTerm.term.description + ".",
        "Success"
      );
    },

    submitEditCourse: function (editedCourse) {
      this.selectedCourse.subjectCode = editedCourse.subjectCode;
      this.selectedCourse.catalogNumber = editedCourse.catalogNumber;
      this.selectedCourse.courseTitle = editedCourse.courseTitle;
      this.selectedCourse.notes = editedCourse.notes;
      this.selectedCourse.units = editedCourse.units;
      this.selectedCourse.contributesToTeaching =
        editedCourse.contributesToTeaching;
      this.selectedCourse.new = editedCourse.new;
      this.$store.commit(
        "workloadForm/updateWorkloadCourses",
        this.workload.courses
      );

      // Toastr  Message
      toastr.options = ToastrSettings;
      toastr.success("Course edited.", "Success");

      this.selectedCourse = null;
      this.editingCourse = false;
    },

    confirmCourseDeletion(timePeriodTerm, course) {
      this.selectedTimePeriodTerm = timePeriodTerm;
      this.selectedCourse = course;
      this.confirmingCourseDeletion = true;
    },

    removeCourse: function () {
      var that = this;
      var courseIndex = _.findIndex(this.workload.courses, function (course) {
        return (
          course.catalogNumber === that.selectedCourse.catalogNumber &&
          course.courseTitle === that.selectedCourse.courseTitle &&
          course.new === that.selectedCourse.new &&
          course.notes === that.selectedCourse.notes &&
          course.subjectCode === that.selectedCourse.subjectCode &&
          course.termId === that.selectedCourse.termId &&
          course.units === that.selectedCourse.units &&
          course.contributesToTeaching ===
          that.selectedCourse.contributesToTeaching
        );
      });
      this.workload.courses.splice(courseIndex, 1);

      this.$store.commit(
        "workloadForm/updateWorkloadCourses",
        this.workload.courses
      );

      // Toastr Message
      toastr.options = ToastrSettings;
      toastr.success(
        "Course removed from " +
        this.selectedTimePeriodTerm.term.description +
        ".",
        "Success"
      );

      this.clearCourseDeleteInfo();
    },

    clearCourseDeleteInfo: function () {
      this.selectedTimePeriodTerm = null;
      this.selectedCourse = null;
      this.selectedCourseIndex = null;
    },

    // this method updates the vuex store when a workload type value is changed
    updateWorkloadTypeValue: function (workloadType, newValue) {
      // manually update the workload type value, then call the store mutation
      if (newValue) {
        var val =
          Math.round((parseFloat(newValue) + Number.EPSILON) * 100) / 100 / 100;

        workloadType.value = val;
      } else {
        workloadType.value = 0;
      }
      this.$store.commit("workloadForm/updateWorkloadTypeValue", workloadType);
    },

    // this method updates the vuex store when a workload type's notes are changed
    updateWorkloadTypeNotes: function (workloadType, newValue) {
      workloadType.notes = newValue;
      this.$store.commit("workloadForm/updateWorkloadTypeNotes", workloadType);
    },

    addFile(files) {
      var that = this;
      this.$emit("fileAdded", files);
      _.forEach(files, function (file) {
        that.files.push(file);
        that.workload.workloadFiles.unshift({
          workloadId: that.workload.workloadId,
          file: {
            fileName: file.name,
            createdBy: that.user.userId,
            createdDate: new Date(),
            _rowVariant: "success"
          }
        });
      });
    },

    removeFile(file) {
      this.$emit("fileRemoved", file);
    }
  },

  watch: {
    workload: {
      // This will let Vue know to look inside the array
      deep: true,

      // We have to move our method to a handler field
      handler() {
        if (this.workload && this.workload.timePeriod) {
          _.forEach(
            this.workload.timePeriod.timePeriodTerms,
            timePeriodTerm => {
              this.overages[timePeriodTerm.termId] =
                _.filter(this.workload.overageRequests, function (req) {
                  return req.termId === timePeriodTerm.termId;
                }).length > 0;
            }
          );
        }
      }
    }
  }
};
</script>

<style>
.workload-type-label,
.control-label.workload-course-label {
  padding-bottom: 0;
  font-weight: bold;
  /* margin-left: 15px; */
}

.total-label {
  font-weight: bold;
}

.no-courses-text {
  font-style: italic;
}

.workload-type-input {
  max-width: 100px;
  margin-left: 15px;
}

.span-feedback.invalid-feedback {
  display: block;
}

.hidden-header {
  display: none;
}

.remove-icon:hover {
  cursor: pointer;
  color: #e04545;
}

.edit-icon:hover {
  cursor: pointer;
  opacity: 0.7;
}
</style>
